export type AutomationRingState = 'none' | 'pending' | 'progress' | 'overdue';

type Props = {
    /** Fraction of this month's automations completed, 0..1. */
    progress?: number;
    state: AutomationRingState;
    size?: number;
};

const SIZE = 24;
const CENTER = SIZE / 2;

/**
 * The "A" badge shown in the topbar, ringed to summarise this month's
 * automations: absent, pending, partly complete, or overdue.
 */
const AutomationStatusRing = ({ progress = 0, state, size = SIZE }: Props) => {
    // A thin hairline when there is nothing to report, thicker once there is.
    const strokeWidth = state === 'none' ? 1 : 2;
    const radius = CENTER - strokeWidth / 2 - 0.5;
    const circumference = 2 * Math.PI * radius;

    const trackColor = state === 'none' ? 'var(--text)' : 'var(--grey500)';
    const arcColor = state === 'overdue' ? 'var(--orange)' : 'var(--green)';

    // Overdue takes the whole ring; otherwise fill by completion.
    const fraction =
        state === 'overdue'
            ? 1
            : state === 'progress'
            ? Math.min(Math.max(progress, 0), 1)
            : 0;

    return (
        <svg
            width={size}
            height={size}
            viewBox={`0 0 ${SIZE} ${SIZE}`}
            fill="none"
            aria-hidden="true"
        >
            <circle
                cx={CENTER}
                cy={CENTER}
                r={radius}
                stroke={trackColor}
                strokeWidth={strokeWidth}
            />
            {fraction > 0 && (
                <circle
                    cx={CENTER}
                    cy={CENTER}
                    r={radius}
                    stroke={arcColor}
                    strokeWidth={strokeWidth}
                    strokeLinecap="round"
                    strokeDasharray={circumference}
                    strokeDashoffset={circumference * (1 - fraction)}
                    // Start the arc at 12 o'clock and run clockwise.
                    transform={`rotate(-90 ${CENTER} ${CENTER})`}
                    style={{ transition: 'stroke-dashoffset 0.3s ease' }}
                />
            )}
            <text
                x={CENTER}
                y={CENTER}
                textAnchor="middle"
                dominantBaseline="central"
                fontSize="12"
                fontWeight="600"
                fill="var(--text)"
            >
                A
            </text>
        </svg>
    );
};

export default AutomationStatusRing;
